import { useGroup } from '@gik/api/inkinds/group';
import { acceptGroupInvitation } from '@gik/api/users/acceptGroupInvitation';
import { useEmailAlreadyTaken } from '@gik/api/users/auth';
import type { IEmailAddressAlreadyTakenResponse } from '@gik/api/users/types';
import { useUser } from '@gik/api/users/user';
import { auth } from '@gik/auth';
import { SignInFlowStep } from '@gik/auth/components/SignInFlow/SignInFlowContent';
import { SignInFlowStartContentFormInputNames } from '@gik/auth/enums/SignInFlowStartContentFormInputNames';
import { ExternalLoginProvider } from '@gik/auth/utils/LoginProvider';
import { signin } from '@gik/auth/utils/auth';
import { fullDefaultPageImagePath } from '@gik/core/constants';
import { withMeta } from '@gik/core/hocs/withMeta';
import type { BreakpointType } from '@gik/core/hooks/CustomHooks';
import { breakpointBem } from '@gik/core/hooks/CustomHooks';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { Group } from '@gik/core/models/gik/Group';
import type { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { ErrorPageSection } from '@gik/core/pages/Error/ErrorPageSection';
import routes from '@gik/core/routes.ts';
import { useUserStore } from '@gik/core/store/UserStore';
import { useCan } from '@gik/core/store/permissions';
import { useBemCN } from '@gik/core/utils/bemBlock';
import noop from '@gik/core/utils/noop';
import replacePath from '@gik/core/utils/replacePath';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { createPageGroupIdParameter } from '@gik/create/components/CreatePage/CreatePage.tsx';
import { useInvitationEmailAddressFromToken } from '@gik/finish-registration/api/FinishRegistrationAPI';
import { useGroupDefaults } from '@gik/inkind-page/hooks/useGroupDefaults';
import { Button } from '@gik/ui-legacy/Button';
import { FlexGrid } from '@gik/ui-legacy/FlexGrid';
import { SearchInput } from '@gik/ui-legacy/SearchInput';
import { UI } from '@gik/ui-legacy/UIManager';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import DocumentAddIcon from '@heroicons/react/outline/DocumentAddIcon';
import DocumentIcon from '@heroicons/react/outline/DocumentIcon';
import debounce from 'lodash/debounce';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useSearchParam from 'react-use/lib/useSearchParam';
import { openAddInkindPagesModal } from '../utils/openAddInkindPagesModal.tsx';
import { InkindGroupHeader } from './InkindGroupHeader.tsx';
import { InkindGroupHero } from './InkindGroupHero.tsx';
import { InkindGroupPagesGrid } from './InkindGroupPagesGrid.tsx';

const inviteRefParameter = 'ref';

export type InkindGroupPageServerProps = {
  group?: Group;
  inkinds?: InkindPageAPIModel[];
  statusCode?: number;
};

export type InkindGroupPageProps = {
  groupSlug?: string;
  muted?: boolean;
  // live-preview override — use this group instead of the SWR fetch
  // so buffered branding edits render without touching the shared cache.
  groupOverride?: Group;
} & BreakpointType;

function InkindGroupPageComp({ groupSlug, breakpoint, muted, groupOverride }: InkindGroupPageProps) {
  const bem = useBemCN('inkind-group-page');

  const [inviteTriggered, setInviteTriggered] = React.useState(false);
  const [loginTriggered, setLoginTriggered] = React.useState(false);
  const [seeMorePagesButtonVisible, setSeeMorePagesButtonVisible] = React.useState(false);
  // const [numResults, setNumResults] = React.useState<number>();
  const [showAllPages, setShowAllPages] = React.useState(false);
  const [search, setSearch] = React.useState<string>('');
  // Bumped after "Add Existing Page" succeeds to force `InkindGroupPagesGrid`
  // to re-run its Algolia search. The grid is search-index backed, not a
  // direct DB read, so a newly added Page can take a moment to appear —
  // retry a few times with a short delay to bridge that indexing lag instead
  // of requiring the user to manually refresh the page.
  const [pagesRefreshToken, setPagesRefreshToken] = React.useState(0);
  // Pages just added via the modal, shown in the grid immediately (Algolia's
  // index lags several seconds); the grid dedupes these against the search
  // results as it catches up.
  const [optimisticallyAddedPages, setOptimisticallyAddedPages] = React.useState<InkindPageAPIModel[]>([]);

  const { data: _group } = useGroup(groupSlug);
  const group = useGroupDefaults(groupOverride ?? _group);

  // Page-management privileges for the group's Pages toolbar/cards.
  // - Adding/removing existing Pages hits `groups.update` on the backend
  //   (see GroupsController), so those are gated on `manage groups`.
  // - Creating a brand-new Page needs `create inkinds`; group managers can
  //   create too, so either privilege unlocks the "Create" button.
  // `useCan` is client-only (false during SSR/first render), so gate the
  // markup behind `isMounted()` to avoid a hydration mismatch — same pattern
  // as `InkindGroupHeader`/`InkindGroupHero`.
  const isMounted = useIsMounted();
  const canManageGroupPages = useCan('manage', 'groups', { groupId: group?.id }) && !!group;
  const canCreatePage = useCan('create', 'inkinds', { groupId: group?.id }) && !!group;
  const showToolbarWhenMounted = isMounted();
  const showCreatePageButton = (canCreatePage || canManageGroupPages) && showToolbarWhenMounted;
  // Add-existing is now a page-level action (backend scopes it to pages the
  // caller manages), so members can add their own pages too — same gate as
  // Create. Backend rejects any page the caller doesn't manage.
  const showAddExistingButton = (canCreatePage || canManageGroupPages) && showToolbarWhenMounted;

  const isMdUp = useBreakpoint(Breakpoint.LG);
  const { t } = useTranslation();

  const inviteRef = useSearchParam(inviteRefParameter);
  const [userId, userRefreshToken] = useUserStore(state => [state.id, state.refreshToken]);
  const { data: user } = useUser(userId);

  const {
    data: { email: invitationEmailAddress } = {
      invitationEmailAddress: undefined,
    },
  } = useInvitationEmailAddressFromToken(inviteRef);
  const { data: emailTakenStatus } = useEmailAlreadyTaken(invitationEmailAddress);

  const finalImageUrl = React.useMemo(() => {
    if (group?.inkindGroupSettings?.logoImageUrl?.length > 0) {
      return group.inkindGroupSettings.logoImageUrl;
    } else if (group?.inkindGroupSettings?.bannerImageUrl?.length > 0) {
      return group.inkindGroupSettings.bannerImageUrl;
    } else {
      return fullDefaultPageImagePath;
    }
  }, [group]);

  const groupDescription = group?.inkindGroupSettings?.description?.trim();

  // "Add Existing Page" — the grid renders from Algolia (not a direct DB
  // read), so a just-added Page won't be in the search index yet. Show the
  // added Pages optimistically right away, and also bump the refresh token a
  // few times so the grid reconciles with Algolia once it has indexed them.
  const handleAddExistingPages = React.useCallback(async () => {
    const addedPages = await openAddInkindPagesModal(group);
    if (!addedPages?.length) return;

    setOptimisticallyAddedPages(prev => {
      const existing = new Set(prev.map(page => page.routeId));
      return [...addedPages.filter(page => !existing.has(page.routeId)), ...prev];
    });

    const retryDelaysMs = [1000, 2000, 3000, 5000];
    for (const delay of retryDelaysMs) {
      await new Promise(resolve => setTimeout(resolve, delay));
      setPagesRefreshToken(token => token + 1);
    }
  }, [group]);

  const acceptInvitation = React.useCallback(
    async function acceptInvitation() {
      if (!invitationEmailAddress || user.emailAddress === invitationEmailAddress) {
        const response = await acceptGroupInvitation(inviteRef, userRefreshToken);

        if (response.ok) {
          const { refreshToken, refreshTokenHash } = (await response.json()) as {
            refreshToken: string;
            refreshTokenHash: string;
          };
          useUserStore.getState().login(refreshToken, refreshTokenHash);
          UI.notifySuccess('you have been successfully added to the group');
        } else {
          UI.notifyError(
            'This invitation does not exist or has already been accepted. Please try logging out and back in.',
            {
              autoClose: false,
            }
          );
        }
      } else {
        if (
          await UI.confirm(
            `You are accepting an invitation to register under email ${
              invitationEmailAddress ? invitationEmailAddress : '<>'
            } but are logged in with a different email (${
              user?.emailAddress || '<>'
            }). We will log you out so you can continue registering your new account.`,
            {
              title: 'Finish Registration',
            }
          )
        ) {
          auth.logout(false);
          window.location.reload();
          return;
        }
      }

      // prevent duplication
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(inviteRefParameter);
      replacePath(urlParams);
    },
    [invitationEmailAddress, inviteRef, user, userRefreshToken]
  );

  const triggerLogin = React.useCallback(async function triggerLogin(
    invitationEmailAddress: string,
    emailTakenStatus: IEmailAddressAlreadyTakenResponse
  ) {
    setLoginTriggered(true);
    // user must be logged in for the invite to work
    // show login or signup screen in this case

    signin({
      startOn: !emailTakenStatus.exist
        ? SignInFlowStep.SIGNUP
        : emailTakenStatus.google || emailTakenStatus.facebook
          ? SignInFlowStep.OAUTH
          : SignInFlowStep.LOGIN,
      initialValues: {
        [SignInFlowStartContentFormInputNames.EmailAddress]: invitationEmailAddress,
        provider: emailTakenStatus.google
          ? ExternalLoginProvider.GOOGLE
          : emailTakenStatus.facebook
            ? ExternalLoginProvider.FACEBOOK
            : null,
        firstName: emailTakenStatus.firstName,
      },
    });
  }, []);

  // TODO: extract these hooks into their own files
  React.useEffect(() => {
    if (inviteRef && !inviteTriggered && user) {
      acceptInvitation();
      setInviteTriggered(true);
    }
  }, [acceptInvitation, inviteRef, inviteTriggered, user]);

  React.useEffect(() => {
    if (!userId && !loginTriggered && inviteRef && invitationEmailAddress && emailTakenStatus) {
      triggerLogin(invitationEmailAddress, emailTakenStatus);
    }
  }, [invitationEmailAddress, inviteRef, loginTriggered, triggerLogin, userId, emailTakenStatus]);

  const HeaderContent = (
    <>
      {!muted && (
        <span {...bem('title', undefined, 'font-heading text-2xl mt-1 font-semibold uppercase ')}>Group InKinds</span>
      )}
      {!muted && (
        <FlexGrid {...bem('toolbar-search')}>
          <SearchInput
            // disabled={!numResults}
            variant="lightgrey"
            clearable
            value={search}
            placeholder="Search Pages"
            onValueChange={debounce(setSearch, 200)}
            {...bem('search')}
          />
        </FlexGrid>
      )}
    </>
  );

  return withMeta(
    {
      title: group?.title,
      description: group?.inkindGroupSettings.description,
      imageUrl: finalImageUrl,
      imageDimensions:
        finalImageUrl === fullDefaultPageImagePath
          ? {
              width: 500,
              height: 500,
            }
          : // if dimensions are undefined, Facebook will try to figure it
            // out by itself but thumbnail generation will be delayed
            undefined,
    },
    <div {...bem(undefined, [breakpointBem(breakpoint), { muted }])}>
      <PageSection noPad {...bem('section-header')} breakpoint={breakpoint}>
        <InkindGroupHeader breakpoint={breakpoint} muted={muted} group={group} />
      </PageSection>
      <PageSection noPad breakpoint={breakpoint}>
        <InkindGroupHero breakpoint={breakpoint} muted={muted} group={group} />
      </PageSection>
      {inviteRef && !userId && (
        <PageSection {...bem('accept-message')} breakpoint={breakpoint}>
          <Button pill onClick={() => signin()}>
            Accept invitation
          </Button>
        </PageSection>
      )}

      <PageSection {...bem('section-content')}>
        <div
          {...bem(
            'toolbar',
            [{ 'no-description': !groupDescription }],
            'flex flex-wrap items-center justify-between gap-4 mb-5 justify-center lg:justify-start'
          )}
        >
          {isMdUp ? <>{HeaderContent}</> : <div className="flex flex-row gap-2 lg:block mt-4">{HeaderContent}</div>}
          {!muted && (
            <div {...bem('toolbar-actions', undefined, 'flex flex-row flex-wrap gap-4 items-center')}>
              {showCreatePageButton && (
                <Button
                  className="flex-1 sm:flex-none"
                  href={routes.create() + `?${createPageGroupIdParameter}=${group.id}`}
                  variant="secondary"
                >
                  <DocumentIcon /> <span className="hidden sm:block">Create New Inkind Page</span>
                  <span className="sm:hidden">Create New</span>
                </Button>
              )}
              {showAddExistingButton && (
                <Button className="flex-1 sm:flex-none" variant="secondary-outline" onClick={handleAddExistingPages}>
                  <DocumentAddIcon />
                  <span className="hidden sm:block">Add An Existing Page</span>
                  <span className="sm:hidden">Add Existing</span>
                </Button>
              )}
            </div>
          )}
        </div>
        {group && (
          <InkindGroupPagesGrid
            showAllPages={showAllPages}
            search={search}
            group={group}
            setSeeMoreButtonVisible={setSeeMorePagesButtonVisible}
            setNumResults={noop /*setNumResults*/}
            breakpoint={breakpoint}
            muted={muted}
            refreshToken={pagesRefreshToken}
            onAddExistingPages={handleAddExistingPages}
            canManagePages={canManageGroupPages}
            canCreatePage={canCreatePage}
            optimisticallyAddedPages={optimisticallyAddedPages}
          />
        )}
        {seeMorePagesButtonVisible && !showAllPages && (
          <footer {...bem('footer')}>
            <Button pill onClick={() => setShowAllPages(true)}>
              Load More
            </Button>
          </footer>
        )}
      </PageSection>
    </div>
  );
}

export const InkindGroupPage = withComponentErrorBoundary(InkindGroupPageComp, {
  ErrorFallback: ErrorPageSection,
});
